import React, { Fragment, useEffect } from 'react';
import { Column, Row } from '@xingternal/xing-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Reason, Version, VersionStatus } from '../../../models/version';
import { RootState } from '../../../redux';
import {
  clonePage,
  fetchAdminPage,
  publishVersion,
  revertVersion,
  saveVersion,
} from '../../../redux/admin/actions';
import { AppRoutes } from '../../../RoutesConfig';
import { AdminNavigation } from './AdminNavigation';
import { PageForm } from './components/PageForm';
import { getInitialValues } from './components/PageForm/form-initial-values';
import { FormValues } from './components/PageForm/types';
import { PageVersions } from './components/PageVersions';
import styles from './TermsEdit.css';

interface AdminTermsEditProps {
  slug: string;
  versionId: number;
  genericVersionId?: number;
  locale: string;
}

const findVersion = (
  versions: Version[],
  versionId: number,
  genericVersionId: number
): Version => {
  return versions.find((v: Version) => {
    if (genericVersionId) {
      return (
        v.id == versionId &&
        genericVersionId == v.genericId &&
        v.reason == Reason.UpdatedByGeneric
      );
    }

    return v.id == versionId && v.reason == Reason.UpdatedByUser;
  });
};

export const AdminTermsEdit = ({
  slug,
  versionId,
  locale,
  genericVersionId,
}: AdminTermsEditProps): React.ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, error, entity: page } = useSelector<
    RootState,
    RootState['adminPageDetails']
  >(state => state.adminPageDetails);

  useEffect(() => {
    dispatch(fetchAdminPage(slug));
  }, [versionId]);

  if (loading) {
    return <span>Loading</span>;
  }

  if (error) {
    return <span>Error</span>;
  }

  if (!page) {
    return null;
  }

  const version = findVersion(page.versions, versionId, genericVersionId);
  if (!version) {
    return null;
  }

  const handleSelectedLanguage = (isTouched: boolean) => (
    newLocale: string
  ): void => {
    if (
      isTouched &&
      !confirm(
        'You have unsaved changes. Do you really want to discard the changes?'
      )
    ) {
      return;
    }

    history.push(
      AppRoutes.admin.adminTermsOverview(slug, version.id, newLocale)
    );
  };

  const isPagePublished = (): boolean =>
    version.status == VersionStatus.published ||
    version.status == VersionStatus.scheduled;

  const onPublishVersion = (): void => {
    dispatch(publishVersion(version));
  };

  const onRevertVersion = (): void => {
    dispatch(revertVersion(version));
  };

  const onSaveVersion = async (values: FormValues): Promise<void> => {
    dispatch(saveVersion(values));
  };

  const onCloneVersion = (): void => {
    dispatch(clonePage(page, locale, history));
  };

  return (
    <Fragment>
      <AdminNavigation enableBackButton enableBorder />
      <Row className={styles.editContainer}>
        <Column size={2}>
          <PageVersions
            page={page}
            versions={page.versions}
            currentVersion={version}
            locale={locale}
          />
        </Column>
        <Column offset={1} size={9}>
          <PageForm
            page={page}
            version={version}
            initialValues={getInitialValues(page, version, locale)}
            readOnly={isPagePublished()}
            onLanguageChanged={handleSelectedLanguage}
            onSaveVersion={onSaveVersion}
            onPublishVersion={onPublishVersion}
            onRevertVersion={onRevertVersion}
            onCloneVersion={onCloneVersion}
            embeddableForm={page.embeddable}
          />
        </Column>
      </Row>
    </Fragment>
  );
};
